import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Tag, Button, message, Input, Spin, Empty } from 'antd';
import {
  AppstoreOutlined,
  CheckCircleFilled,
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  DeploymentUnitOutlined,
  DatabaseOutlined,
  ApiOutlined,
  ToolOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import './index.css';
import {
  MarketplaceAgent,
  listMarketplaceAgents,
  loadMarketplaceAgent,
  unloadMarketplaceAgent,
} from '../../services/agentMarketplaceApi';

const { Text, Title } = Typography;

const TYPES = [
  { key: 'all', label: '全部智件', icon: '✨', color: '#0ea5e9', desc: '平台已上架业务智能体' },
  { key: 'business', label: '业务智能体', icon: '🤖', color: '#1677ff', desc: '可加载到业务智舱直接使用' },
  { key: 'ops', label: '运维管理', icon: '🛡️', color: '#13c2c2', desc: '健康检查、日志诊断、告警处置' },
  { key: 'data', label: '数据分析', icon: '📊', color: '#52c41a', desc: '数据洞察与报表分析' },
  { key: 'service', label: '服务编排', icon: '🔌', color: '#722ed1', desc: 'API、MCP 与工具服务编排' },
];

function getAgentIcon(agent: MarketplaceAgent) {
  const text = `${agent.category || ''}${agent.subCategory || ''}${agent.name || ''}${agent.agentCode || ''}`;
  if (/运维|监控|告警|ops|operation/i.test(text)) return <DeploymentUnitOutlined />;
  if (/数据|知识|分析|检索/.test(text)) return <DatabaseOutlined />;
  if (/接口|api|服务|网关|mcp/i.test(text)) return <ApiOutlined />;
  if (/工具|组件|编排/.test(text)) return <ToolOutlined />;
  return <RobotOutlined />;
}

function getTypeInfo(agent: MarketplaceAgent) {
  const text = `${agent.category || ''}${agent.subCategory || ''}${agent.name || ''}${agent.agentCode || ''}`;
  if (/运维|监控|告警|ops|operation/i.test(text)) return TYPES.find(item => item.key === 'ops')!;
  if (/数据|知识|分析|检索/.test(text)) return TYPES.find(item => item.key === 'data')!;
  if (/接口|api|服务|网关|mcp/i.test(text)) return TYPES.find(item => item.key === 'service')!;
  return TYPES.find(item => item.key === 'business')!;
}

export default function SkillMarketPage() {
  const [typeFilter, setTypeFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [agents, setAgents] = useState<MarketplaceAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingCode, setPendingCode] = useState<string>();

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const rows = await listMarketplaceAgents({ keyword: keyword.trim() || undefined });
      setAgents(rows);
    } catch (error: any) {
      message.error(`加载智件超市失败：${error?.response?.data?.message || error?.message || '未知错误'}`);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => agents.filter(agent => {
    if (typeFilter !== 'all' && getTypeInfo(agent).key !== typeFilter) return false;
    const q = keyword.trim().toLowerCase();
    if (!q) return true;
    return `${agent.name} ${agent.description} ${agent.agentCode} ${agent.developerName || ''} ${(agent.tags || []).join(' ')}`.toLowerCase().includes(q);
  }), [agents, keyword, typeFilter]);

  const loadedAgents = agents.filter(agent => agent.loaded);

  const toggleAgent = async (agent: MarketplaceAgent) => {
    setPendingCode(agent.agentCode);
    try {
      if (agent.loaded) {
        await unloadMarketplaceAgent(agent.agentCode);
        message.success('已从业务智舱移除');
      } else {
        await loadMarketplaceAgent(agent.agentCode);
        message.success('已加载到业务智舱');
      }
      await fetchAgents();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || '操作失败');
    } finally {
      setPendingCode(undefined);
    }
  };

  return (
    <div className="skill-market-page" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f7f8fa' }}>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', flexShrink: 0 }}>
        <AppstoreOutlined style={{ fontSize: 20, color: '#fff' }} />
        <Title level={5} style={{ margin: 0, color: '#fff' }}>智件超市</Title>
        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginLeft: 8 }}>加载真实业务智能体到业务智舱，支持版本、权限和调用留痕</Text>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索智能体名称、能力、开发者"
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            onPressEnter={fetchAgents}
            style={{ maxWidth: 420, borderRadius: 14 }}
          />
          <Button icon={<SearchOutlined />} onClick={fetchAgents}>查询</Button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 12, color: '#0ea5e9', marginBottom: 8, display: 'block' }}>智能体类型</Text>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {TYPES.map(type => {
              const on = typeFilter === type.key;
              const count = type.key === 'all' ? agents.length : agents.filter(agent => getTypeInfo(agent).key === type.key).length;
              return (
                <div key={type.key} className={`skill-type-card ${on ? 'active' : ''}`} onClick={() => setTypeFilter(type.key)} style={{
                  cursor: 'pointer', padding: '10px 18px', borderRadius: 10, minWidth: 132,
                  border: on ? `2px solid ${type.color}` : '1px solid #f0f0f0',
                  background: on ? `${type.color}10` : '#fff', transition: 'all .2s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 16 }}>{type.icon}</span>
                    <Text strong style={{ fontSize: 13, color: on ? type.color : '#333' }}>{type.label}</Text>
                    <Tag style={{ fontSize: 10, margin: 0, marginLeft: 'auto', background: on ? type.color + '20' : '#f5f5f5', color: on ? type.color : '#999', border: 'none' }}>{count}</Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: 10 }}>{type.desc}</Text>
                </div>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="skill-empty-panel" style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 10 }}>
            <Spin />
            <div style={{ marginTop: 12 }}><Text type="secondary">正在读取真实智件超市数据...</Text></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="skill-empty-panel" style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 10 }}>
            <Empty description="暂无匹配的已上架智能体" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14, marginBottom: 20 }}>
            {filtered.map(agent => {
              const typeInfo = getTypeInfo(agent);
              return (
                <div key={agent.id} className="skill-market-card" style={{
                  background: '#fff', borderRadius: 10, padding: '18px 20px',
                  border: `1px solid ${agent.loaded ? '#86efac' : typeInfo.color + '40'}`,
                  borderLeft: `4px solid ${agent.loaded ? '#22c55e' : typeInfo.color}`,
                  transition: 'all .2s', cursor: 'default',
                }}>
                  <div style={{ marginBottom: 8 }}>
                    <Tag color={typeInfo.color} style={{ fontSize: 10, margin: 0, borderRadius: 4 }}>{typeInfo.icon} {typeInfo.label}</Tag>
                    <Tag color={agent.status === 'listed' ? 'green' : 'default'} style={{ fontSize: 10, margin: 0, marginLeft: 4, borderRadius: 4 }}>{agent.status === 'listed' ? '已上架' : agent.status || '未知'}</Tag>
                    {agent.version && <Tag color="blue" style={{ fontSize: 10, margin: 0, marginLeft: 4, borderRadius: 4 }}>v{agent.version}</Tag>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `${typeInfo.color}15`, color: typeInfo.color, fontSize: 18, flexShrink: 0 }}>{getAgentIcon(agent)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text strong style={{ fontSize: 14 }}>{agent.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>{agent.developerName || '智坊平台'} · {agent.subCategory || agent.category || '业务智能体'}</Text>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>{agent.description}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
                    {(agent.tags || []).map(tag => <Tag key={tag} style={{ fontSize: 10, margin: 0, borderRadius: 4 }}>{tag}</Tag>)}
                    {agent.capabilities?.streaming && <Tag style={{ fontSize: 10, margin: 0, borderRadius: 4 }}>流式响应</Tag>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Button size="small" type={agent.loaded ? 'default' : 'primary'} loading={pendingCode === agent.agentCode}
                      icon={agent.loaded ? <CheckCircleFilled /> : <PlusOutlined />}
                      onClick={() => toggleAgent(agent)}
                      style={agent.loaded ? { color: '#22c55e', borderColor: '#bbf7d0' } : { background: typeInfo.color, borderColor: typeInfo.color }}>
                      {agent.loaded ? '已加载' : '加载到业务智舱'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="skill-connected-panel" style={{ background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: loadedAgents.length > 0 ? 10 : 0 }}>
            <Text strong style={{ fontSize: 14, color: '#0ea5e9' }}><ThunderboltOutlined /> 已加载到业务智舱</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {loadedAgents.length === 0 ? '暂未加载，业务智舱将显示纯展示空状态' : `已加载 ${loadedAgents.length} 个智能体，可在业务智舱中对话`}
            </Text>
          </div>
          {loadedAgents.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {loadedAgents.map(agent => <Tag key={agent.id} color="green" icon={<CheckCircleFilled />}>{agent.name}</Tag>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
